// AuthForm.tsx

import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
  type AuthError,
} from "firebase/auth";
import { auth } from "@/firebase";
import { GoogleIcon } from "./GoogleIcon";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const AuthForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const [tabIndex, setTabIndex] = useState(0); // 0 = Sign In, 1 = Register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    setError(null);
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      const googleProvider = new GoogleAuthProvider();
      await signInWithPopup(auth, googleProvider);
      const redirectTo = searchParams.get("redirect") || "/";
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const authError = err as AuthError;
      if (authError.code === "auth/popup-closed-by-user") {
        return;
      }
      console.error("Google sign in error:", authError);
      setError(authError.message || t("components.authForm.errors.default"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (tabIndex === 1) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        if (displayName.trim()) {
          await updateProfile(userCredential.user, { displayName });
        }
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        const redirectTo = searchParams.get("redirect") || "/";
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      const authError = err as AuthError;
      switch (authError.code) {
        case "auth/email-already-in-use":
          setError(t("components.authForm.errors.emailInUse"));
          break;
        case "auth/invalid-credential":
        case "auth/wrong-password":
        case "auth/user-not-found":
          setError(t("components.authForm.errors.invalidCredentials"));
          break;
        case "auth/weak-password":
          setError(t("components.authForm.errors.weakPassword"));
          break;
        default:
          setError(authError.message || t("components.authForm.errors.default"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs">
      {/* Paper utilizes the global overrides specified in theme.ts automatically */}
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Typography component="h1" variant="h5" gutterBottom sx={{ textAlign: "center" }}>
          {tabIndex === 0 ? t("components.authForm.welcomeBack") : t("components.authForm.createAccount")}
        </Typography>

        <Tabs value={tabIndex} onChange={handleTabChange} variant="fullWidth" sx={{ mb: 3 }}>
          <Tab label={t("components.authForm.signIn")} />
          <Tab label={t("components.authForm.register")} disabled />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Google Sign-In Option - Uses secondary/outline theme styling */}
        <Button
          fullWidth
          variant="outlined"
          color="secondary"
          startIcon={<GoogleIcon />}
          onClick={handleGoogleSignIn}
          // disabled={loading}
          disabled
          sx={{
            py: 1.2,
            fontSize: "0.95rem",
            borderColor: "divider",
            color: "text.primary",
            "&:hover": {
              borderColor: "secondary.main",
              backgroundColor: "action.hover",
            },
          }}
        >
          {tabIndex === 0 ? t("components.authForm.signInWithGoogle") : t("components.authForm.signUpWithGoogle")}
        </Button>

        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("components.authForm.or")}
          </Typography>
        </Divider>

        {/* Email/Password Form */}
        <Box component="form" onSubmit={handleSubmit}>
          {tabIndex === 1 && (
            <TextField
              margin="normal"
              required
              fullWidth
              label={t("components.authForm.fullName")}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={loading}
            />
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            label={t("components.authForm.emailAddress")}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            label={t("components.authForm.password")}
            type="password"
            autoComplete={tabIndex === 0 ? "current-password" : "new-password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />

          {/* Primary Button automatically maps to your custom primary color (#0077cc / #80BFFF) */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{
              mt: 3,
              mb: 2,
              py: 1.2,
              fontSize: "1rem",
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : tabIndex === 0 ? (
              t("components.authForm.signIn")
            ) : (
              t("components.authForm.register")
            )}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};
